/**
 * Separation-profile landmarker — the geodesic-field hand analysis of ADR-010
 * §2 (Phase B) / §3 (`kernel/hands` = "hand topology analysis and augmentation
 * math"). ONE responsibility: over a {@link VoxelGrid}, run a wrist-seeded
 * geodesic distance field, build the level-component merge graph, and classify
 * WHERE (if at all) the volume individuates into digits — the separation
 * profile `hand-5/4/3/partial/mitten/none`. It places NO joints and creates NO
 * bones (that is a later PR, ADR-010 scope guard); it emits the persistent
 * branch chains (base→tip level-component centroids) a joint-placement PR will
 * consume, plus a named EVIDENCE list so nothing is silent (ADR-010 §7).
 *
 * Design record: docs/research/proposals/hand-landmarker/formal-treatment.md
 * (geodesic + error budget §1, separation profile + merge graph §2, persistence
 * §3, thumb §4, confidence §6, fixtures §7), accepted with reservations in
 * REVIEW-2026-07-11.md. This module implements the treatment AS WRITTEN plus the
 * review's BINDING corrections R1–R5, which OVERRIDE the reference where they
 * conflict:
 *  - R5: retain ALL adjacent level-(k−1) merge parents (not just the largest),
 *    so a wrist-connected volume that splits around a void and rejoins (a ring
 *    with one distal continuation) is ONE terminal branch, not phantom digits;
 *    merge cycles (join nodes) are named evidence and degrade confidence.
 *  - R2: the §4 thumb criterion is an optional PRE-FILL that fires only when its
 *    margins genuinely hold (earliest-separation margin ≥ 2 steps AND angular
 *    divergence beyond the direction-uncertainty bound). `high` confidence keys
 *    on a USER-CONFIRMED thumb (ADR-010 §11); automatic results cap at `medium`.
 *  - R3: `hand-mitten` never asserts VERIFIED structure — a symmetric two-branch
 *    volume is surfaced with an `ambiguous-profile` marker + low confidence, not
 *    a confident mitten.
 *  - R4: an enforced two-empty-layer robustness gate on the classification-
 *    critical two-branch separation (named `sub-resolution-gap`); the
 *    `ambiguous-profile` near-25%-boundary marker; and an explicit, named >5-
 *    branch disposition.
 *  - R1: golden geometry only (the delivered `three-with-stub` fixture's probe
 *    fused into the thumb) — see separation-profile.test.ts.
 *
 * Rejections are NAMED RESULTS, not thrown exceptions: an honest "this is not a
 * hand" (`hand-none`), a broken field (`disconnected-grid`), a seed off the
 * volume (`invalid-grid`), or too-few geodesic layers (`insufficient-
 * resolution`) are product outcomes the Hand Studio ribbon shows (ADR-010 §7,
 * §11), not bugs. Only a STRUCTURAL grid-contract violation throws
 * (InvalidVoxelGridError, from voxel-grid.ts) — the line is drawn there.
 *
 * Determinism (ADR-005 §9): sorted seeds, FIFO BFS, ascending-index component
 * seeds, and persistence-then-id branch ordering make repeat runs deep-equal and
 * the classification invariant under axis permutation/reflection.
 *
 * Depends on: hands/voxel-grid. Depended on by: kernel index, a future joint-
 * placement PR, and (through the public API) app/hand-studio.
 *
 * Credit: the accepted core machinery (geodesic field, 26/6 level-component
 * split, merge-graph persistence, degradations, rejections) originates from GPT
 * 5.6 Sol's fourth hand-landmarker delivery (per the review's credit note),
 * reimplemented in kernel style and integrated with the R1–R5 corrections.
 */
import {
  validateVoxelGrid,
  voxelCoords,
  voxelIndex,
  type VoxelGrid,
  type VoxelPoint,
} from './voxel-grid.js';

export type SeparationProfile =
  | 'hand-5'
  | 'hand-4'
  | 'hand-3'
  | 'hand-partial'
  | 'hand-mitten'
  | 'hand-none';

export type Confidence = 'low' | 'medium' | 'high';

/**
 * How many digits a profile CLAIMS, or `null` when it claims no count.
 *
 * `hand-partial` is the one that matters: it is the "does not match any of the
 * above" bucket, reached either by an anomalous branch count OR — at five branches
 * or fewer — by a persistence-DEPTH judgement that says nothing about how many
 * digits there are. Treating it as a count is what made the android's left hand
 * refuse a correct five-digit declaration (see {@link SeparationResult} `declared`).
 * `hand-mitten` claims two lobes, which is a count, and is checked as one.
 */
export function declaredDigitCount(profile: SeparationProfile): number | null {
  switch (profile) {
    case 'hand-5':
      return 5;
    case 'hand-4':
      return 4;
    case 'hand-3':
      return 3;
    case 'hand-mitten':
      return 2;
    case 'hand-none':
      return 0;
    case 'hand-partial':
      return null;
  }
}

export type RejectionReason =
  | 'invalid-grid'
  | 'disconnected-grid'
  | 'insufficient-resolution'
  | 'hand-none';

/**
 * A named evidence marker — the machine-checkable form of "nothing silent"
 * (ADR-010 §7). Every degradation, rejection cause, dismissed branch, and
 * near-boundary condition gets one; `detail` carries the measured numbers.
 */
export interface EvidenceMarker {
  readonly code: string;
  readonly detail?: Readonly<Record<string, number | string | boolean | readonly number[]>>;
}

/**
 * One persistent branch: a digit candidate the joint-placement PR will consume.
 * `chain` is the base→tip sequence of level-component centroids (the medial-curve
 * estimator, treatment §5); `baseLevel` is the merge distance where it separated.
 * No joints/ratios here (out of scope).
 */
export interface BranchChain {
  readonly id: string;
  /** Geodesic level where this branch separated from its siblings (the merge distance). */
  readonly baseLevel: number;
  readonly tipLevel: number;
  /** Consecutive levels it existed as a distinct component (treatment §3). */
  readonly persistenceVoxels: number;
  /** Level-component centroids, base→tip (voxel-index space). */
  readonly chain: readonly VoxelPoint[];
  /** Max component radius along the branch — the centroid-vs-medial-axis bias floor (§5). */
  readonly centroidBiasVoxels: number;
  /** §4/§11: true=thumb, false=non-thumb once a thumb is fixed, null=none identified. */
  readonly thumb: boolean | null;
}

export interface SeparationEvidence {
  readonly markers: readonly EvidenceMarker[];
  readonly distanceLayers: number;
  readonly maxDistanceVoxels: number;
  /** S(k): the count of merge-graph nodes (level components) per geodesic level (§2). */
  readonly separationProfile: readonly number[];
  readonly terminalBranches: number;
  readonly persistentBranches: number;
  readonly rejectedStubs: number;
  readonly medianPersistenceVoxels: number | null;
  readonly medianPersistenceRatio: number | null;
  readonly centroidBiasVoxels: number;
  /** Error floors reported as evidence, never used to erase branches (§1, §3). */
  readonly metricRelativeUpperBound: number;
  readonly endpointQuantizationVoxels: number;
  readonly voxelSize: number;
  readonly minRobustEmptyLayers: number;
  readonly mergeCyclesCollapsed: number;
  readonly thumbAmbiguous: boolean;
}

export type SeparationResult =
  | {
      readonly kind: 'hand';
      /** The MEASURED profile — the volume's own reading. A declaration never rewrites it. */
      readonly profile: Exclude<SeparationProfile, 'hand-none'>;
      readonly branches: readonly BranchChain[];
      /** The identified thumb branch id, or null when unresolved (§4/§11/R2). */
      readonly thumb: string | null;
      readonly confidence: Confidence;
      /**
       * The user's taxonomy CLAIM held beside the measurement (§11 "fail-loud both
       * directions"), or null when none was declared. BOTH are always kept and
       * nothing is ever re-labeled — `profile` above still reports what the volume
       * read.
       *
       * `agrees` asks the question that MATTERS DOWNSTREAM: can the declaration be
       * built from the branches this extraction actually found? It is therefore a
       * comparison of DIGIT COUNTS ({@link declaredDigitCount}), not of label
       * strings. That distinction is load-bearing and was got wrong once, on a real
       * specimen: the android's left hand at resolution 24 yields FIVE branches but
       * classifies `hand-partial`, because `hand-partial` at ≤5 branches is a
       * PERSISTENCE-DEPTH judgement ("these are shallow relative to the hand") and
       * not a count claim at all. Under label equality the user answered "5 digits",
       * the panel drew five branches, and the graft refused — an engine overruling a
       * human on the one question §11 reserves for the human. Under count
       * comparison it places five digits from five branches and fabricates nothing.
       *
       * The fabrication guards are untouched, because they were always count
       * disagreements: declaring `hand-4` over five branches (the kobold's phantom
       * finger) and declaring `hand-5` over four still contradict and still refuse.
       * `hand-partial` asserts no count, so it agrees with any branch set — which is
       * exactly what "does not match any of the above" means.
       */
      readonly declared: {
        readonly profile: SeparationProfile;
        readonly agrees: boolean;
      } | null;
      readonly evidence: SeparationEvidence;
    }
  | {
      readonly kind: 'rejected';
      readonly reason: RejectionReason;
      readonly needs: string;
      readonly evidence: SeparationEvidence;
    };

export interface SeparationOptions {
  /**
   * ADR-010 §11 one-tap thumb confirmation: the user-declared thumb branch id.
   * A CONFIRMED thumb is what unlocks `high` confidence (R2); the automatic §4
   * pre-fill caps at `medium`. A confirmation that matches no branch (e.g. the
   * tapped branch was re-classified as a stub) is surfaced as a loud
   * `thumb-confirmation-unmatched` marker, never silently dropped.
   */
  readonly confirmedThumbBranchId?: string;
  /**
   * ADR-010 §11 profile declaration: the user's TAXONOMY CLAIM about this hand —
   * the editable profile chip, pre-filled with the measured best guess. It is a
   * CLAIM, never an override: it can corroborate the volume or contradict it, but
   * it NEVER re-labels the measurement (see {@link SeparationResult.declared}).
   *
   * Why a taxonomy claim and not a finger COUNT (Allen, 2026-07-15): a count
   * conflates DESIGN INTENT with RIGGABLE TOPOLOGY. Asked "how many fingers?", a
   * user looking at a mitten with four fingers sculpted into it answers "four" —
   * and a count field cannot hold "intent = 4 AND topology = one fused paddle",
   * which are BOTH true. Trusting that count would constrain extraction to four
   * chains and FABRICATE three fingers that no volume supports — the same failure
   * this field exists to prevent, merely user-induced. `hand-4` and `hand-mitten`
   * are distinct named categories, so a taxonomy claim holds the pair honestly.
   * (The UI asks in plain language — "this hand looks like a mitten; are the
   * fingers fused together?" — and resolves the answer to this field; a person
   * judges topology by looking, never jargon.)
   *
   * Agreement → `profile-user-confirmed` + unlocks `high` (same authority as a
   * confirmed thumb). Contradiction → `profile-declaration-contradicts-volume`,
   * both sides retained, confidence forced `low`: exactly one of the two is
   * wrong and the volume cannot arbitrate its own case. Downstream, `placeDigits`
   * REFUSES an unresolved contradiction rather than fabricate.
   */
  readonly declaredProfile?: SeparationProfile;
}

/** Persistence floor: 3 geodesic levels. Below is a `sub-resolution` stub (§3). */
const PERSISTENCE_MIN = 3;
/** Median-persistence / Dmax boundary separating deep digits from distal-only (§3). */
const PARTIAL_RATIO = 0.25;
/** Robust separation needs ≥2 empty voxel layers between surfaces (§1). */
const MIN_ROBUST_EMPTY_LAYERS = 2;
/** A hand has at most five digits — above this the branch count is anomalous (R4). */
const MAX_DIGITS = 5;
/**
 * Unreachable-voxel share the analysis tolerates as spatial DEBRIS (dropped +
 * named `unreachable-debris-dropped`) rather than rejecting `disconnected-grid`.
 * Sized from the measured case: the barbarian-R bracer ring is ~5% of the hand
 * region; a genuinely broken field is far past this.
 */
const DEBRIS_MAX_FRACTION = 0.1;
const METRIC_RELATIVE_UPPER_BOUND = Math.sqrt(3) - 1;
const ENDPOINT_QUANTIZATION_VOXELS = Math.sqrt(3) / 2;

/** 6-connectivity offsets — the conservative propagation neighborhood (§1). */
const FACE_NEIGHBORS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

/** 26-connectivity offsets — the level-component neighborhood (§2). */
const CELL_NEIGHBORS: readonly (readonly [number, number, number])[] = (() => {
  const out: [number, number, number][] = [];
  for (let a = -1; a <= 1; a += 1) {
    for (let b = -1; b <= 1; b += 1) {
      for (let c = -1; c <= 1; c += 1) {
        if (a !== 0 || b !== 0 || c !== 0) out.push([a, b, c]);
      }
    }
  }
  return out;
})();

interface MergeNode {
  readonly id: number;
  readonly k: number;
  readonly vox: readonly number[];
  readonly parents: number[];
  readonly children: number[];
}

interface RawBranch {
  readonly leafId: number;
  readonly baseLevel: number;
  readonly tipLevel: number;
  readonly persistence: number;
  readonly chain: readonly VoxelPoint[];
  readonly centroidBias: number;
  readonly allVox: readonly number[];
  readonly segment: VoxelPoint;
  readonly segmentLength: number;
}

function centroid(vox: readonly number[], dims: readonly [number, number, number]): VoxelPoint {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const i of vox) {
    const [px, py, pz] = voxelCoords(i, dims);
    x += px;
    y += py;
    z += pz;
  }
  const n = vox.length;
  return { x: x / n, y: y / n, z: z / n };
}

function euclid(a: VoxelPoint, b: VoxelPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function unit(v: VoxelPoint): VoxelPoint {
  const len = Math.hypot(v.x, v.y, v.z);
  return len > 1e-9 ? { x: v.x / len, y: v.y / len, z: v.z / len } : { x: 0, y: 0, z: 0 };
}

function angleBetween(a: VoxelPoint, b: VoxelPoint): number {
  const ua = unit(a);
  const ub = unit(b);
  const d = Math.max(-1, Math.min(1, ua.x * ub.x + ua.y * ub.y + ua.z * ub.z));
  return Math.acos(d);
}

/** Deterministic 6-connectivity BFS from the sorted seed set over occupied voxels. */
function geodesicField(grid: VoxelGrid): Map<number, number> {
  const { dims, occupied } = grid;
  const dist = new Map<number, number>();
  const queue: number[] = [...grid.wristSeed].sort((a, b) => a - b);
  for (const s of queue) dist.set(s, 0);
  for (let head = 0; head < queue.length; head += 1) {
    const i = queue[head]!;
    const di = dist.get(i)!;
    const [x, y, z] = voxelCoords(i, dims);
    for (const [a, b, c] of FACE_NEIGHBORS) {
      const nx = x + a;
      const ny = y + b;
      const nz = z + c;
      if (nx < 0 || ny < 0 || nz < 0 || nx >= dims[0] || ny >= dims[1] || nz >= dims[2]) continue;
      const j = voxelIndex(nx, ny, nz, dims);
      if (occupied.has(j) && !dist.has(j)) {
        dist.set(j, di + 1);
        queue.push(j);
      }
    }
  }
  return dist;
}

/** 26-connected components among the voxels at exactly distance `k`, ascending-index seeded. */
function levelComponents(
  levelVoxels: readonly number[],
  dims: readonly [number, number, number],
): number[][] {
  const set = new Set(levelVoxels);
  const seen = new Set<number>();
  const comps: number[][] = [];
  for (const seed of levelVoxels) {
    if (seen.has(seed)) continue;
    const comp: number[] = [seed];
    seen.add(seed);
    for (let head = 0; head < comp.length; head += 1) {
      const [x, y, z] = voxelCoords(comp[head]!, dims);
      for (const [a, b, c] of CELL_NEIGHBORS) {
        const nx = x + a;
        const ny = y + b;
        const nz = z + c;
        if (nx < 0 || ny < 0 || nz < 0 || nx >= dims[0] || ny >= dims[1] || nz >= dims[2]) continue;
        const j = voxelIndex(nx, ny, nz, dims);
        if (set.has(j) && !seen.has(j)) {
          seen.add(j);
          comp.push(j);
        }
      }
    }
    comps.push(comp.sort((p, q) => p - q));
  }
  return comps;
}

/**
 * Build the merge graph with ALL adjacent level-(k−1) parents retained (R5).
 * Each level's 26-components become nodes; a node links to every level-(k−1)
 * node it is 26-adjacent to. A node with >1 parent is a JOIN — the discrete
 * signature of a volume that split and rejoined (a loop).
 */
function buildMergeGraph(
  grid: VoxelGrid,
  dist: Map<number, number>,
  maxDistance: number,
): MergeNode[] {
  const { dims } = grid;
  const byLevel: number[][] = Array.from({ length: maxDistance + 1 }, () => []);
  for (const [i, d] of dist) byLevel[d]!.push(i);

  const nodes: MergeNode[] = [];
  const voxelNode = new Map<number, number>();
  for (let k = 0; k <= maxDistance; k += 1) {
    const levelVoxels = byLevel[k]!.sort((a, b) => a - b);
    for (const comp of levelComponents(levelVoxels, dims)) {
      const node: MergeNode = { id: nodes.length, k, vox: comp, parents: [], children: [] };
      nodes.push(node);
      for (const i of comp) voxelNode.set(i, node.id);
    }
  }

  for (const node of nodes) {
    if (node.k === 0) continue;
    const parents = new Set<number>();
    for (const i of node.vox) {
      const [x, y, z] = voxelCoords(i, dims);
      for (const [a, b, c] of CELL_NEIGHBORS) {
        const nx = x + a;
        const ny = y + b;
        const nz = z + c;
        if (nx < 0 || ny < 0 || nz < 0 || nx >= dims[0] || ny >= dims[1] || nz >= dims[2]) continue;
        const pid = voxelNode.get(voxelIndex(nx, ny, nz, dims));
        if (pid !== undefined && nodes[pid]!.k === node.k - 1) parents.add(pid);
      }
    }
    node.parents.push(...[...parents].sort((a, b) => a - b));
    for (const pid of node.parents) nodes[pid]!.children.push(node.id);
  }
  return nodes;
}

/**
 * Extract one branch per leaf (childless node, k>0), walking base-ward until a
 * split (parent with >1 child) or a JOIN (a node with >1 parent — where a loop
 * rejoined). Multi-parent awareness is the R5 fix: the arms of a ring are not
 * leaves (they feed the join), so only true tips become branches — no phantom
 * digits. Persistence = levels the branch existed as a distinct component (§3).
 */
function extractBranches(nodes: readonly MergeNode[], dims: readonly [number, number, number]): RawBranch[] {
  const leaves = nodes.filter((n) => n.children.length === 0 && n.k > 0);
  const branches = leaves.map((leaf): RawBranch => {
    const path: MergeNode[] = [leaf];
    let n = leaf;
    while (true) {
      if (n.parents.length !== 1) break; // join (>1 parent) or root — branch begins here
      const parent = nodes[n.parents[0]!]!;
      if (parent.children.length > 1) break; // split below — branch begins at n
      path.push(parent);
      n = parent;
    }
    path.reverse();
    const persistence = leaf.k - path[0]!.k + 1;
    const chain = path.map((p) => centroid(p.vox, dims));
    let centroidBias = 0;
    for (const p of path) {
      const c = centroid(p.vox, dims);
      for (const i of p.vox) {
        const [px, py, pz] = voxelCoords(i, dims);
        centroidBias = Math.max(centroidBias, euclid(c, { x: px, y: py, z: pz }));
      }
    }
    const allVox = path.flatMap((p) => p.vox);
    const base = chain[0]!;
    const tip = chain[chain.length - 1]!;
    const segment = { x: tip.x - base.x, y: tip.y - base.y, z: tip.z - base.z };
    return {
      leafId: leaf.id,
      baseLevel: path[0]!.k,
      tipLevel: leaf.k,
      persistence,
      chain,
      centroidBias,
      allVox,
      segment,
      segmentLength: Math.hypot(segment.x, segment.y, segment.z),
    };
  });
  return branches.sort((a, b) => b.persistence - a.persistence || a.leafId - b.leafId);
}

/** Direction-uncertainty half-cone for a branch of a given segment length (§4). */
function directionUncertainty(voxelSize: number, segmentLength: number): number {
  return 2 * Math.asin(Math.min(1, voxelSize / Math.max(segmentLength, 1e-9)));
}

/** Min Chebyshev separation between two branches' voxel sets → empty layers = gap−1 (§1). */
function emptyLayersBetween(
  a: readonly number[],
  b: readonly number[],
  dims: readonly [number, number, number],
): number {
  let minCheby = Infinity;
  for (const ia of a) {
    const [ax, ay, az] = voxelCoords(ia, dims);
    for (const ib of b) {
      const [bx, by, bz] = voxelCoords(ib, dims);
      const cheby = Math.max(Math.abs(ax - bx), Math.abs(ay - by), Math.abs(az - bz));
      if (cheby < minCheby) minCheby = cheby;
    }
  }
  return minCheby - 1;
}

function separationProfileOf(nodes: readonly MergeNode[], maxDistance: number): number[] {
  const counts = new Array<number>(maxDistance + 1).fill(0);
  for (const n of nodes) counts[n.k] = (counts[n.k] ?? 0) + 1;
  return counts;
}

function reject(
  reason: RejectionReason,
  needs: string,
  markers: EvidenceMarker[],
  partial: Partial<SeparationEvidence>,
): SeparationResult {
  return {
    kind: 'rejected',
    reason,
    needs,
    evidence: {
      markers: [{ code: reason }, ...markers],
      distanceLayers: partial.distanceLayers ?? 0,
      maxDistanceVoxels: partial.maxDistanceVoxels ?? 0,
      separationProfile: partial.separationProfile ?? [],
      terminalBranches: partial.terminalBranches ?? 0,
      persistentBranches: partial.persistentBranches ?? 0,
      rejectedStubs: partial.rejectedStubs ?? 0,
      medianPersistenceVoxels: partial.medianPersistenceVoxels ?? null,
      medianPersistenceRatio: partial.medianPersistenceRatio ?? null,
      centroidBiasVoxels: partial.centroidBiasVoxels ?? 0,
      metricRelativeUpperBound: METRIC_RELATIVE_UPPER_BOUND,
      endpointQuantizationVoxels: ENDPOINT_QUANTIZATION_VOXELS,
      voxelSize: partial.voxelSize ?? 0,
      minRobustEmptyLayers: MIN_ROBUST_EMPTY_LAYERS,
      mergeCyclesCollapsed: partial.mergeCyclesCollapsed ?? 0,
      thumbAmbiguous: partial.thumbAmbiguous ?? true,
    },
  };
}

/**
 * Analyze a voxel grid into its separation profile. See the module header for
 * the full contract; rejections are named results, structural grid violations
 * throw (from validateVoxelGrid).
 */
export function analyzeSeparationProfile(
  grid: VoxelGrid,
  options: SeparationOptions = {},
): SeparationResult {
  // Fail-loud STRUCTURAL boundary (throws). The named-result boundary is below.
  validateVoxelGrid(grid);
  const { dims, voxelSize, occupied } = grid;

  // Named result `invalid-grid`: the seed indices are all valid voxels but at
  // least one misses the occupied volume — the wrist cut floats off the hand.
  // A correctable product outcome (ADR-010 §11 adjustable wrist cut), not a crash.
  const seedOffVolume = [...grid.wristSeed].filter((s) => !occupied.has(s)).sort((a, b) => a - b);
  if (seedOffVolume.length > 0) {
    return reject('invalid-grid', 'wrist-seed voxels that lie on the occupied volume', [
      { code: 'wrist-seed-off-volume', detail: { seeds: seedOffVolume } },
    ], { voxelSize });
  }

  const dist = geodesicField(grid);
  let workGrid = grid;
  // Producer liberties (grid.repairs — e.g. a re-banded wrist seed) surface in
  // the evidence stream alongside the analyzer's own markers.
  const debrisMarkers: EvidenceMarker[] = [...(grid.repairs ?? [])];
  if (dist.size !== occupied.size) {
    const unreachable = occupied.size - dist.size;
    if (unreachable > DEBRIS_MAX_FRACTION * occupied.size) {
      return reject(
        'disconnected-grid',
        'one wrist-connected hand volume (unreachable voxels present)',
        [{ code: 'unreachable-voxels', detail: { reachable: dist.size, occupied: occupied.size } }],
        { voxelSize },
      );
    }
    // A SMALL unreachable remainder is spatial debris, not a broken field —
    // measured cause: a bracer-edge ring of hand-dominated flesh proximal of
    // the wrist plane (barbarian R, 2026-07-28), which cost the whole hand
    // under the strict gate. It is dropped from the working volume and NAMED;
    // beyond DEBRIS_MAX_FRACTION the honest rejection above stands.
    workGrid = { ...grid, occupied: new Set(dist.keys()) };
    debrisMarkers.push({
      code: 'unreachable-debris-dropped',
      detail: { droppedVoxels: unreachable, occupiedVoxels: occupied.size },
    });
  }

  let maxDistance = 0;
  for (const d of dist.values()) maxDistance = Math.max(maxDistance, d);
  if (maxDistance < 4) {
    // Enforced pre-analysis resolution gate (§1): fewer than four geodesic
    // layers cannot robustly host a two-empty-layer separation.
    return reject(
      'insufficient-resolution',
      'at least four geodesic layers beyond the wrist (re-voxelize the hand region higher)',
      [{ code: 'below-resolution-floor', detail: { maxDistanceVoxels: maxDistance } }],
      { voxelSize, maxDistanceVoxels: maxDistance, distanceLayers: maxDistance + 1 },
    );
  }

  const nodes = buildMergeGraph(workGrid, dist, maxDistance);
  const sk = separationProfileOf(nodes, maxDistance);
  const mergeCyclesCollapsed = nodes.filter((n) => n.parents.length > 1).length;

  const rawBranches = extractBranches(nodes, dims);
  const persistent = rawBranches.filter((b) => b.persistence >= PERSISTENCE_MIN);
  const stubs = rawBranches.filter((b) => b.persistence < PERSISTENCE_MIN);
  const overallBias = persistent.reduce((m, b) => Math.max(m, b.centroidBias), 0);

  const baseMarkers: EvidenceMarker[] = [...debrisMarkers];
  // Dismissed branches are NAMED, never silently dropped (ADR-010 §7).
  for (const s of stubs) {
    baseMarkers.push({
      code: 'rejected-stub',
      detail: { leafId: s.leafId, persistenceVoxels: s.persistence, baseLevel: s.baseLevel },
    });
  }
  if (mergeCyclesCollapsed > 0) {
    // R5: a ring that split and rejoined. All-parent retention already collapsed
    // it to its true terminal branch(es); we name it and degrade confidence.
    baseMarkers.push({ code: 'merge-cycle-collapsed', detail: { joins: mergeCyclesCollapsed } });
  }

  const evidenceBase = {
    distanceLayers: maxDistance + 1,
    maxDistanceVoxels: maxDistance,
    separationProfile: sk,
    terminalBranches: rawBranches.length,
    persistentBranches: persistent.length,
    rejectedStubs: stubs.length,
    centroidBiasVoxels: overallBias,
    metricRelativeUpperBound: METRIC_RELATIVE_UPPER_BOUND,
    endpointQuantizationVoxels: ENDPOINT_QUANTIZATION_VOXELS,
    voxelSize,
    minRobustEmptyLayers: MIN_ROBUST_EMPTY_LAYERS,
    mergeCyclesCollapsed,
  };

  if (persistent.length < 2) {
    // Fewer than two persistent branches: no defensible digit structure (§3).
    return reject(
      'hand-none',
      'at least two persistent distal branches, or higher resolution',
      baseMarkers,
      {
        ...evidenceBase,
        medianPersistenceVoxels: persistent.length > 0 ? persistent[0]!.persistence : null,
        medianPersistenceRatio:
          persistent.length > 0 ? persistent[0]!.persistence / maxDistance : null,
        thumbAmbiguous: true,
      },
    );
  }

  // Median persistence and the 25% deep-vs-distal boundary (§3).
  const sortedPersist = [...persistent].map((b) => b.persistence).sort((a, b) => a - b);
  const medianPersistence = sortedPersist[Math.floor(persistent.length / 2)]!;
  const medianRatio = medianPersistence / maxDistance;
  const isPartial = medianRatio < PARTIAL_RATIO;
  // Near-boundary: within one persistence layer of the 0.25·Dmax boundary (§3's
  // "within one metric-error step" — one geodesic layer is the smallest step).
  const ambiguousProfile = Math.abs(medianPersistence - PARTIAL_RATIO * maxDistance) <= 1;

  // --- profile label (R3/R4 dispositions) ---
  const markers = [...baseMarkers];
  let profile: Exclude<SeparationProfile, 'hand-none'>;
  let excessBranches = false;
  if (persistent.length === 2) {
    profile = 'hand-mitten';
  } else if (persistent.length <= MAX_DIGITS) {
    profile = isPartial
      ? 'hand-partial'
      : persistent.length === 5
        ? 'hand-5'
        : persistent.length === 4
          ? 'hand-4'
          : 'hand-3';
  } else {
    // R4 explicit >5 disposition: a hand has ≤5 digits, so >5 persistent branches
    // is anomalous (creature digits, jewelry, mesh debris — ADR-010 §11 anomaly
    // mask). Label `hand-partial` (the only multi-digit "does-not-cleanly-match"
    // bucket), name the TRUE count, keep ALL branches for the consumer + user
    // correction (never silently truncate to 5), and cap confidence low.
    profile = 'hand-partial';
    excessBranches = true;
    markers.push({ code: 'anomalous-branch-count', detail: { persistentBranches: persistent.length } });
  }

  // --- R4 two-empty-layer robustness gate (enforced, classification-critical) ---
  // Scoped to the two-branch regime, where the whole "two digits vs one paddle"
  // reading hinges on a single separation. For ≥3 branches the structure is
  // over-determined and legitimately-adjacent fingers (1 empty column apart at
  // fixture resolution, as in clean-five) must NOT be flagged — that would demote
  // the happy path. The marker distinguishes a sub-resolution split from an
  // ordinary ambiguous (robust-gap) mitten (R4/Codex PR #9).
  let subResolutionGap = false;
  if (persistent.length === 2) {
    const layers = emptyLayersBetween(persistent[0]!.allVox, persistent[1]!.allVox, dims);
    if (layers < MIN_ROBUST_EMPTY_LAYERS) {
      subResolutionGap = true;
      markers.push({ code: 'sub-resolution-gap', detail: { emptyLayers: layers, needsEmptyLayers: MIN_ROBUST_EMPTY_LAYERS } });
    }
  }

  // --- R3: a mitten never asserts VERIFIED structure without a divergent branch ---
  if (profile === 'hand-mitten') {
    const pairAngle = angleBetween(persistent[0]!.segment, persistent[1]!.segment);
    const bound = Math.max(
      directionUncertainty(voxelSize, persistent[0]!.segmentLength),
      directionUncertainty(voxelSize, persistent[1]!.segmentLength),
    );
    if (pairAngle <= bound) {
      markers.push({
        code: 'ambiguous-profile',
        detail: { reason: 'mitten-structure-unverified', pairAngleRad: pairAngle, uncertaintyBoundRad: bound },
      });
    }
  } else if (ambiguousProfile) {
    markers.push({
      code: 'ambiguous-profile',
      detail: { reason: 'near-partial-boundary', medianPersistenceRatio: medianRatio, boundary: PARTIAL_RATIO },
    });
  }

  // --- §4 thumb identification: user-confirmed (unlocks high) > auto pre-fill (caps medium) ---
  let thumbLeafId: number | null = null;
  let thumbConfirmed = false;
  if (options.confirmedThumbBranchId !== undefined) {
    const idxOf = persistent.findIndex((_, i) => `branch-${i + 1}` === options.confirmedThumbBranchId);
    if (idxOf >= 0) {
      thumbLeafId = persistent[idxOf]!.leafId;
      thumbConfirmed = true;
      markers.push({ code: 'thumb-user-confirmed', detail: { branchId: options.confirmedThumbBranchId } });
    } else {
      // Loud, never silent (ADR-010 §11): the tapped branch is gone this run.
      markers.push({ code: 'thumb-confirmation-unmatched', detail: { branchId: options.confirmedThumbBranchId } });
    }
  }
  if (thumbLeafId === null) {
    const auto = thumbPrefill(persistent, voxelSize);
    if (auto !== null) {
      thumbLeafId = auto;
      markers.push({ code: 'thumb-prefill', detail: { leafId: auto } });
    }
  }
  const thumbAmbiguous = thumbLeafId === null;
  if (thumbAmbiguous) markers.push({ code: 'thumb-ambiguous' });

  // --- branches for the consumer, base→tip, ordered by persistence ---
  const branches: BranchChain[] = persistent.map((b, i) => ({
    id: `branch-${i + 1}`,
    baseLevel: b.baseLevel,
    tipLevel: b.tipLevel,
    persistenceVoxels: b.persistence,
    chain: b.chain,
    centroidBiasVoxels: b.centroidBias,
    thumb: thumbLeafId === null ? null : b.leafId === thumbLeafId,
  }));
  const thumbBranchId = thumbLeafId === null ? null : branches[persistent.findIndex((b) => b.leafId === thumbLeafId)]!.id;

  // --- §11 profile declaration: the user's CLAIM vs the volume's EVIDENCE ---
  // The claim never re-labels the measurement ("fail-loud both directions"):
  // agreement corroborates, a contradiction is RECORDED with both sides retained.
  // A contradiction means exactly one of the two is wrong, and the volume cannot
  // arbitrate its own case — so confidence drops to `low` and placeDigits refuses.
  let declared: { profile: SeparationProfile; agrees: boolean } | null = null;
  if (options.declaredProfile !== undefined) {
    // Count, not label — see the `declared` field docs for the specimen that
    // proved the difference. A declaration asserting no count agrees with anything.
    const claimed = declaredDigitCount(options.declaredProfile);
    const agrees = claimed === null || claimed === branches.length;
    declared = { profile: options.declaredProfile, agrees };
    if (!agrees) {
      markers.push({
        code: 'profile-declaration-contradicts-volume',
        detail: { declared: options.declaredProfile, measured: profile },
      });
    } else if (options.declaredProfile === profile) {
      markers.push({ code: 'profile-user-confirmed', detail: { profile } });
    } else {
      // The counts line up but the labels do not: the human overrode a LABEL the
      // volume was never confident about. Corroboration of the count is not
      // corroboration of the shape, so this gets its own marker rather than
      // borrowing `profile-user-confirmed` — and `degraded` still holds confidence
      // down, because a shallow five is exactly what we are unsure about.
      markers.push({
        code: 'profile-label-overridden-count-agrees',
        detail: { declared: options.declaredProfile, measured: profile, branches: branches.length },
      });
    }
  }

  // --- confidence (§6, R2): degradations force low; else confirmed thumb → high, automatic → medium ---
  const degraded =
    profile === 'hand-partial' ||
    profile === 'hand-mitten' ||
    stubs.length > 0 ||
    subResolutionGap ||
    mergeCyclesCollapsed > 0 ||
    ambiguousProfile ||
    excessBranches;
  // A contradicted declaration forces `low` alongside the measured degradations; a
  // confirmed one carries the same authority as a confirmed thumb (a human read the
  // volume and agreed). Degradation still dominates: confirming the LABEL does not
  // vouch for the BRANCHES the extraction found.
  const confidence: Confidence =
    degraded || declared?.agrees === false ? 'low' : thumbConfirmed || declared?.agrees === true ? 'high' : 'medium';

  return {
    kind: 'hand',
    profile,
    branches,
    thumb: thumbBranchId,
    confidence,
    declared,
    evidence: {
      markers,
      ...evidenceBase,
      medianPersistenceVoxels: medianPersistence,
      medianPersistenceRatio: medianRatio,
      thumbAmbiguous,
    },
  };
}

/**
 * §4 thumb PRE-FILL (R2): fires only when the earliest-separating branch is ALSO
 * the most angularly divergent AND both margins genuinely hold — a merge-distance
 * margin of ≥2 steps to the next branch, and divergence from the other branches'
 * mean direction beyond that branch's direction-uncertainty bound. Returns the
 * thumb leaf id, or null (ambiguous — a symmetric mitten cannot tell thumb from
 * pinky from volume alone). Never over-claims: identity, not placement.
 */
function thumbPrefill(persistent: readonly RawBranch[], voxelSize: number): number | null {
  if (persistent.length < 2) return null;
  const byBase = [...persistent].sort((a, b) => a.baseLevel - b.baseLevel || a.leafId - b.leafId);
  const candidate = byBase[0]!;
  const margin = byBase[1]!.baseLevel - byBase[0]!.baseLevel;
  if (margin < 2) return null;

  // Candidate must be the single most-divergent branch from the overall mean.
  const meanAll = unit(
    persistent.reduce(
      (acc, b) => {
        const s = unit(b.segment);
        return { x: acc.x + s.x, y: acc.y + s.y, z: acc.z + s.z };
      },
      { x: 0, y: 0, z: 0 } as VoxelPoint,
    ),
  );
  const divs = persistent.map((b) => angleBetween(b.segment, meanAll));
  const maxDiv = Math.max(...divs);
  if (angleBetween(candidate.segment, meanAll) < maxDiv - 1e-9) return null;

  // Divergence from the OTHER branches' mean must clear the candidate's bound.
  const others = persistent.filter((b) => b !== candidate);
  const meanOthers = unit(
    others.reduce(
      (acc, b) => {
        const s = unit(b.segment);
        return { x: acc.x + s.x, y: acc.y + s.y, z: acc.z + s.z };
      },
      { x: 0, y: 0, z: 0 } as VoxelPoint,
    ),
  );
  const divergence = angleBetween(candidate.segment, meanOthers);
  const bound = directionUncertainty(voxelSize, candidate.segmentLength);
  return divergence > bound ? candidate.leafId : null;
}
